import { DatadogProvider } from '@datadog/openfeature-browser';
import { OpenFeature } from '@openfeature/web-sdk';

import constants from '../../../constants.json';

export type InitializeDatadogFeatureFlagsOptions = {
  enableExposureLogging?: boolean;
  enableFlagEvaluationTracking?: boolean;
  enableRumFeatureFlagTracking?: boolean;
};

export const DATADOG_OPENFEATURE_DOMAIN = 'datadog';

const DATADOG_CLIENT_TOKEN = 'pubd9df1ad418728dbcd3a4f39e2899261c';

const datadogProviderState = {
  isInitialized: false,
  latestInitializeToken: 0,
};

const defaultInitializeOptions: Required<InitializeDatadogFeatureFlagsOptions> = {
  enableExposureLogging: true,
  enableFlagEvaluationTracking: true,
  enableRumFeatureFlagTracking: true,
};

let currentInitializeOptions: Required<InitializeDatadogFeatureFlagsOptions> | undefined;

function disableDatadogProvider(warningMessage?: string): void {
  datadogProviderState.latestInitializeToken += 1;
  datadogProviderState.isInitialized = false;

  if (warningMessage && process.env.NODE_ENV === 'development') {
    console.warn(warningMessage);
  }
}

// Datadog feature flag environments do not support wildcards, so dev-like
// environments are intentionally collapsed into a single `dev` target.
function getDatadogEnvironment(): string {
  const resolvedEnvironment =
    constants.ENV_NAME === 'production'
      ? window.location.hostname.endsWith('.portal.flipdishdev.com')
        ? window.location.hostname.replace('.portal.flipdishdev.com', '')
        : 'production'
      : constants.ENV_NAME;

  return resolvedEnvironment === 'production' || resolvedEnvironment === 'prod-staging'
    ? resolvedEnvironment
    : 'dev';
}

function normalizeInitializeOptions(
  options: InitializeDatadogFeatureFlagsOptions = {}
): Required<InitializeDatadogFeatureFlagsOptions> {
  return {
    ...defaultInitializeOptions,
    ...options,
  };
}

function hasSameInitializeOptions(
  options: Required<InitializeDatadogFeatureFlagsOptions>
): boolean {
  return (
    currentInitializeOptions?.enableExposureLogging === options.enableExposureLogging &&
    currentInitializeOptions?.enableFlagEvaluationTracking ===
      options.enableFlagEvaluationTracking &&
    currentInitializeOptions?.enableRumFeatureFlagTracking === options.enableRumFeatureFlagTracking
  );
}

export function initializeDatadogFeatureFlags(
  options: InitializeDatadogFeatureFlagsOptions = {}
): void {
  const applicationId = process.env.VITE_DATADOG_APPLICATION_ID;

  if (!applicationId) {
    currentInitializeOptions = undefined;
    disableDatadogProvider(
      "[FeatureFlags] Datadog feature flags are disabled because 'VITE_DATADOG_APPLICATION_ID' is missing."
    );
    return;
  }

  const normalizedOptions = normalizeInitializeOptions(options);

  if (datadogProviderState.isInitialized && hasSameInitializeOptions(normalizedOptions)) {
    return;
  }

  currentInitializeOptions = normalizedOptions;
  datadogProviderState.isInitialized = true;
  const initializationToken = ++datadogProviderState.latestInitializeToken;

  const provider = new DatadogProvider({
    applicationId,
    clientToken: DATADOG_CLIENT_TOKEN,
    enableExposureLogging: normalizedOptions.enableExposureLogging,
    enableFlagEvaluationTracking: normalizedOptions.enableFlagEvaluationTracking,
    env: getDatadogEnvironment(),
    enableRumFeatureFlagTracking: normalizedOptions.enableRumFeatureFlagTracking,
    service: 'web-portal',
    site: 'us3.datadoghq.com',
    version: constants.VERSION,
  });

  void OpenFeature.setProviderAndWait(DATADOG_OPENFEATURE_DOMAIN, provider).catch(
    (error: unknown) => {
      if (initializationToken !== datadogProviderState.latestInitializeToken) {
        return;
      }
      datadogProviderState.isInitialized = false;
      console.error('[FeatureFlags] Failed to initialize Datadog OpenFeature provider.', error);
    }
  );
}
